import React, {useEffect, useRef, useState} from "react";
import {Button, DatePicker, Form, Radio, Row, Select} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {getBookingIncrement, roundToBookingIncrement} from "../helpers/booking";
import {buildCarDescription, getCars} from "../helpers/car";
import {swapDeployments} from "../helpers/deployment";
import {messageType, showMessages} from "../helpers/message";

import {formInCardLayout, formItemFullWidth} from "../assets/styles";
import './DeploymentCreateForm.less';
import {dateTimeFormat, timeDisplayFormat, timestampFormat} from "../lib/time";

const SwapDeploymentCreateForm = props => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const [carData, setCarData] = useState([]);
    const [carID, setCarID] = useState(null);
    const [carLoading, setCarLoading] = useState(true);

    const [form] = Form.useForm();
    const deployableID = props.carID;
    const deployableType = 'book_car';

    //region Set Booking Increment
    const bookingIncrement = getBookingIncrement();
    const bookingIncrementMinutes = Number(bookingIncrement) / 60;
    const timePickerProps = {
        showTime: {
            minuteStep: bookingIncrementMinutes,
            use12Hours: true,
            format: timeDisplayFormat,
        },
        format: dateTimeFormat,
    };

    //endregion
    //region Load Car

    useEffect(() => {
        const populateCarSelect = async () => {

            const abortSignal = abortSignalRef.current;

            try {
                const response = await getCars(1, 999999, 'number', abortSignal);
                if (response) {
                    setCarData(response.data);
                    setCarLoading(false);
                }

            } catch (error) {
                showMessages(error.message, messageType.error);
                setCarLoading(false);
            }
        };
        populateCarSelect();

    }, []);

    //endregion
    //region Cleanup

    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    //endregion
    //region Form functions

    const [startedAt, setStartedAt] = useState(null);
    const [endedAt, setEndedAt] = useState(null);
    const [endTimeEnabled, setEndTimeEnabled] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const onSwapTypeChange = e => {
        // When permanent type is selected, disable end time field and null-ify the end time value
        if (e.target.value === 'permanent') {
            setEndTimeEnabled(false);
            setEndedAt(null);

            form.resetFields(['End Date & Time']);
        } else if (e.target.value === 'temporary') {    // Re-enable end time when temporary type is selected
            setEndTimeEnabled(true);
        }
    }

    const onCarChange = (value) => {
        setCarID(value);
    };

    const onStartTimeChange = (value) => {
        value = value ? roundToBookingIncrement(value,'up') : null;

        const date = value ? value.startOf('minute').format(timestampFormat) : null;

        form.setFieldsValue({
            "Start Date & Time": value
        });
        setStartedAt(date);
    };

    const onEndTimeChange = (value) => {
        value = value ? roundToBookingIncrement(value,'up') : null;

        const date = value ? value.startOf('minute').format(timestampFormat) : null;

        form.setFieldsValue({
            "End Date & Time": value
        });
        setEndedAt(date);
    };

    const handleFinish = async (values) => {
        if (endedAt !== null && !isStartedAtBeforeEndedAt()) {
            showMessages('Please select an End Date & Time that is after the Start Date & Time!', messageType.error);
            return;
        }

        const abortSignal = abortSignalRef.current;

        try {
            setFormSubmitted(true);
            const response = await swapDeployments({
                deployableAID: deployableID,
                deployableBID: carID,
                newStartedAt: startedAt,
                newEndedAt: endedAt,
                deployableType
            }, abortSignal);
            if (response) {
                setFormSubmitted(false);
                showMessages('Vehicle (and bookings) have successfully been swapped in the system!', messageType.success);
            }

        } catch (error) {
            setFormSubmitted(false);
            showMessages(error.message, messageType.error);
        }
    };

    //endregion
    //region Helpers

    const isStartedAtBeforeEndedAt = () => {
        const startedAtObj = dayjs(startedAt).tz();
        const endedAtObj = dayjs(endedAt).tz();

        if (startedAtObj.isBefore(endedAtObj)) {
            return true;
        }

        return false;
    };

    //endregion

    return (
        <Form
            {...formInCardLayout}
            form={form}
            name="deployment-create"
            onFinish={handleFinish}
            initialValues={{
                'Swap Type': 'permanent',
            }}
            style={{width: '100%'}}
        >
            <Form.Item
                name="Swap Type"
                label="Swap Type"
                rules={[{required: true}]}
            >
                <Radio.Group
                    name="swapType"
                    onChange={onSwapTypeChange}
                >
                    <Radio value="permanent" className="radio-button">Permanent Swap</Radio>
                    <Radio value="temporary" className="radio-button">Temporary Swap</Radio>
                </Radio.Group>
            </Form.Item>
            <Form.Item
                name="Car"
                label="Car"
                rules={[{required: true}]}
            >
                <Select
                    showSearch
                    placeholder='Select or search'
                    loading={carLoading}
                    onChange={onCarChange}
                    filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    dropdownMatchSelectWidth={false}
                    className="deployment-car-select"
                >
                    {carData.map((car) => (
                        <Select.Option key={car.id} value={car.id}>
                            {buildCarDescription(car)}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                name="Start Date & Time"
                label="Start Date & Time"
                rules={[{type: 'object', required: true}]}
            >
                <DatePicker
                    {...timePickerProps}
                    placeholder=""
                    onChange={onStartTimeChange}
                    style={{...formItemFullWidth}}
                />
            </Form.Item>
            <Form.Item
                name="End Date & Time"
                label="End Date & Time"
                rules={[{type: 'object', required: endTimeEnabled}]}
            >
                <DatePicker
                    {...timePickerProps}
                    placeholder=""
                    disabled={!endTimeEnabled}
                    onChange={onEndTimeChange}
                    style={{...formItemFullWidth}}
                />
            </Form.Item>
            <Form.Item>
                <Row type="flex" justify="end">
                    <Button type="primary" htmlType="submit" loading={formSubmitted}>
                        Swap Vehicles
                    </Button>
                </Row>
            </Form.Item>
        </Form>
    );
}

SwapDeploymentCreateForm.propTypes = {
    carID: PropTypes.number.isRequired,
}

export default SwapDeploymentCreateForm;
